import React, { useCallback, useRef } from 'react';
import {
  DropdownList,
  DropdownListProps
} from '../../dropdown/DropdownList/DropdownList';
import {
  DropdownPopper,
  DropdownPopperProps
} from '../../dropdown/DropdownPopper/DropdownPopper';
import {
  getDisplayTextOfSelectOption,
  getKeyOfSelectOption,
  SelectOption,
  useSelectOptions
} from './useSelectOptions';
import { prepareProps } from '../../utils/propUtils';
import { InputLayout, InputLayoutProps } from '../InputLayout/InputLayout';
import { useSelectDropdownVisible } from './useSelectDropdownVisible';
import { useHighlightedSelectOption } from './useHighlightedSelectOption';
import { useCombinedEventCallbacks } from '../../hooks/useCombinedEventCallbacks';
import { useEventCallback } from '../../hooks/useEventCallback';
import { Icon, IconType } from '../../misc/Icon/Icon';
import { classes } from '../../utils/utils';
import sharedStyles from '../sharedStyles.module.css';
import styles from './SearchableSelect.module.css';

export type SearchableSelectProps<T> = Omit<InputLayoutProps, 'children'> &
  Pick<
    DropdownListProps<T>,
    'getKeyOfItem' | 'getDisplayTextOfItem' | 'items'
  > & {
    value: T;
    onValueChanged: (value: T) => void;
  };

export const SearchableSelect = React.memo(
  <T,>(props: SearchableSelectProps<T>) => {
    const {
      value,
      items,
      getKeyOfItem,
      getDisplayTextOfItem,
      onValueChanged,
      label,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const inputElementRef = useRef<HTMLElement | null>(null);
    const dropdownElementRef = useRef<HTMLElement | null>(null);

    const { options, currentOption } = useSelectOptions({
      value,
      items,
      getKeyOfItem,
      getDisplayTextOfItem
    });

    const {
      dropdownVisible,
      handleInputClick,
      handleInputKeydown: handleInputKeydownDropdownVisible,
      handleOptionSelected: handleOptionSelectedDropdownVisible
    } = useSelectDropdownVisible({ inputElementRef, dropdownElementRef });

    const handleOptionSelected = useEventCallback((option: SelectOption<T>) => {
      if (option.item !== value) {
        onValueChanged(option.item);
      }

      handleOptionSelectedDropdownVisible();
    });

    const {
      highlightedOption,
      handleInputKeydown: handleInputKeydownHighlight
    } = useHighlightedSelectOption({
      options,
      currentOption,
      active: dropdownVisible,
      onOptionSelected: handleOptionSelected
    });

    const handleInputKeydown = useCombinedEventCallbacks(
      handleInputKeydownDropdownVisible,
      handleInputKeydownHighlight
    );

    const renderPopperContent: DropdownPopperProps['renderContent'] =
      useCallback(
        ({ ref }) => (
          <DropdownList
            ref={ref}
            items={options}
            highlightedItem={highlightedOption ?? undefined}
            getKeyOfItem={getKeyOfSelectOption}
            getDisplayTextOfItem={getDisplayTextOfSelectOption}
            onItemSelected={handleOptionSelected}
            data-testid="SearchableSelect.DropdownList"
          ></DropdownList>
        ),
        [options, highlightedOption, handleOptionSelected]
      );

    const renderInput: InputLayoutProps['children'] = useCallback(() => {
      return (
        <DropdownPopper
          visible={dropdownVisible}
          renderContent={renderPopperContent}
          childElementRef={inputElementRef}
          contentElementRef={dropdownElementRef}
        >
          {({ ref }) => (
            <div className={styles.inputAndIconWrapper}>
              <input
                ref={ref}
                type="text"
                className={classes(sharedStyles.inputBaseStyle, styles.input)}
                value={currentOption.displayText}
                onClick={handleInputClick}
                onKeyDown={handleInputKeydown}
                data-testid="SearchableSelect.Input"
              ></input>
              <div className={styles.iconContainer}>
                <Icon
                  name="caret-down"
                  type={IconType.Solid}
                ></Icon>
              </div>
            </div>
          )}
        </DropdownPopper>
      );
    }, [
      currentOption,
      dropdownVisible,
      renderPopperContent,
      handleInputClick,
      handleInputKeydown,
      inputElementRef,
      dropdownElementRef
    ]);

    return (
      <InputLayout
        label={label}
        {...remainingProps}
      >
        {renderInput}
      </InputLayout>
    );
  }
) as <T>(props: SearchableSelectProps<T>) => JSX.Element;
